import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { HamburgerIcon } from '../common/HamburgerIcon';

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobileMenuOpen]);

  return (
    <header className="border-b bg-white sticky top-0 z-10">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-4">
          {/* Hamburger button - mobile only */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <HamburgerIcon isOpen={isMobileMenuOpen} className="text-gray-600" />
          </button>

          <Link to="/" className="text-lg sm:text-xl font-bold text-gray-900">
            Premium React Loaders
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex gap-6">
            <Link
              to="/playground"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Playground
            </Link>
            <Link
              to="/gallery"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Gallery
            </Link>
            <Link
              to="/docs"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Documentation
            </Link>
            <a
              href="https://docs.premium-react-loaders.ishansasika.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Storybook
            </a>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <a
            href="https://github.com/ishansasika/premium-react-loaders"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-gray-900 transition-colors"
            aria-label="GitHub"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd"
              />
            </svg>
          </a>
          <a
            href="https://www.npmjs.com/package/premium-react-loaders"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            npm
          </a>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          {/* Menu drawer */}
          <nav className="fixed inset-y-0 left-0 w-64 bg-white z-50 p-6 shadow-xl md:hidden transform transition-transform duration-300 ease-in-out">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Menu</h2>
              <button
                onClick={closeMobileMenu}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex flex-col space-y-4">
              <Link
                to="/playground"
                onClick={closeMobileMenu}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors px-3 py-2 hover:bg-gray-100 rounded-lg"
              >
                Playground
              </Link>
              <Link
                to="/gallery"
                onClick={closeMobileMenu}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors px-3 py-2 hover:bg-gray-100 rounded-lg"
              >
                Gallery
              </Link>
              <Link
                to="/docs"
                onClick={closeMobileMenu}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors px-3 py-2 hover:bg-gray-100 rounded-lg"
              >
                Documentation
              </Link>
              <a
                href="https://docs.premium-react-loaders.ishansasika.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors px-3 py-2 hover:bg-gray-100 rounded-lg"
              >
                Storybook
              </a>
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
